import { ApiResponse } from "../models/api-response";
import { callApi } from "./api.service";

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

// --- Interfaces matching Backend Schemas ---

export interface CostDetailEntry {
  timestamp: string; // Timestamps are often strings in JSON
  message: string;
  price: number;
}

export interface CostResponse {
  job_id: string;
  total_price: number;
  details: CostDetailEntry[];
}

export interface JobCostSummaryResponse {
  job_id: string;
  job_title: string;
  total_price: number;
  company_name?: string;
  creator_email?: string;
}

// --- Service Functions ---

/**
 * Fetches the detailed cost breakdown for a specific job offer.
 * @param accessToken - The user's access token.
 * @param jobId - The ID of the job offer.
 * @returns Promise resolving to an ApiResponse containing the cost details.
 */
export const getJobCostDetails = async (
  accessToken: string,
  jobId: string
): Promise<ApiResponse<CostResponse>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<CostResponse>(`${apiServerUrl}/api/pricing/jobs/${jobId}/cost`, config);
};

/**
 * Fetches a summary of total costs for all job offers owned by the user.
 * @param accessToken - The user's access token.
 * @returns Promise resolving to an ApiResponse containing a list of job cost summaries.
 */
export const getJobCostSummaries = async (
  accessToken: string
): Promise<ApiResponse<JobCostSummaryResponse[]>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return callApi<JobCostSummaryResponse[]>(`${apiServerUrl}/api/pricing/costs/summary`, config);
}; 